import React, { useState } from 'react';
import { Document, Page } from 'react-pdf';
import { useDashboardStore } from '../stores/dashboardStore';
import './Resume.css';

// 设置 PDF.js worker
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// 设置 PDF.js worker
import * as pdfjs from 'pdfjs-dist';
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const Resume: React.FC = () => {
    const { config } = useDashboardStore();
    const [selectedLanguage, setSelectedLanguage] = useState<'chinese' | 'english'>('chinese');
    const [numPages, setNumPages] = useState<number | null>(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    const handleLanguageChange = (language: 'chinese' | 'english') => {
        setSelectedLanguage(language);
        setPageNumber(1);
    };

    const handleZoomIn = () => {
        setScale(prev => Math.min(prev + 0.2, 2.0));
    };

    const handleZoomOut = () => {
        setScale(prev => Math.max(prev - 0.2, 0.5));
    };

    const handleResetZoom = () => {
        setScale(1.0);
    };

    const goToPrevPage = () => {
        setPageNumber(prev => Math.max(prev - 1, 1));
    };

    const goToNextPage = () => {
        setPageNumber(prev => Math.min(prev + 1, numPages || 1));
    };

    const downloadResume = () => {
        // 检查文件是否存在
        const filePath = selectedLanguage === 'chinese' ? config.resume.chinese : config.resume.english;
        const link = document.createElement('a');
        link.href = filePath;
        link.download = `resume-${selectedLanguage}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="resume-page">
            <div className="container">
                <div className="resume-header">
                    <h1>简历</h1>
                    <p>点击下载按钮可以下载PDF版本的简历</p>
                </div>

                <div className="resume-controls">
                    <div className="language-selector">
                        <label>选择语言：</label>
                        <div className="language-buttons">
                            <button
                                className={`lang-btn ${selectedLanguage === 'chinese' ? 'active' : ''}`}
                                onClick={() => handleLanguageChange('chinese')}
                            >
                                中文简历
                            </button>
                            <button
                                className={`lang-btn ${selectedLanguage === 'english' ? 'active' : ''}`}
                                onClick={() => handleLanguageChange('english')}
                            >
                                English Resume
                            </button>
                        </div>
                    </div>

                    <div className="pdf-controls">
                        <div className="zoom-controls">
                            <button onClick={handleZoomOut} className="control-btn" title="缩小">
                                −
                            </button>
                            <span className="zoom-level">{Math.round(scale * 100)}%</span>
                            <button onClick={handleZoomIn} className="control-btn" title="放大">
                                +
                            </button>
                            <button onClick={handleResetZoom} className="control-btn" title="重置">
                                ↺
                            </button>
                        </div>

                        <div className="page-controls">
                            <button
                                onClick={goToPrevPage}
                                disabled={pageNumber <= 1}
                                className="control-btn"
                                title="上一页"
                            >
                                ‹
                            </button>
                            <span className="page-info">
                                第 {pageNumber} 页，共 {numPages || 0} 页
                            </span>
                            <button
                                onClick={goToNextPage}
                                disabled={pageNumber >= (numPages || 1)}
                                className="control-btn"
                                title="下一页"
                            >
                                ›
                            </button>
                        </div>

                        <button onClick={downloadResume} className="download-btn">
                            📥 下载简历
                        </button>
                    </div>
                </div>

                <div className="pdf-container">
                    <div className="pdf-viewer">
                        <Document
                            file={selectedLanguage === 'chinese' ? config.resume.chinese : config.resume.english}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={
                                <div className="loading">
                                    <div className="spinner"></div>
                                    <p>正在加载简历...</p>
                                </div>
                            }
                            error={
                                <div className="error">
                                    <p>❌ 无法加载简历文件</p>
                                    <p>请确保已添加以下文件：</p>
                                    <ul>
                                        <li>public/files/resume-zh.pdf (中文简历)</li>
                                        <li>public/files/resume-en.pdf (英文简历)</li>
                                    </ul>
                                    <p>您可以将PDF文件拖拽到对应目录中</p>
                                </div>
                            }
                        >
                            <Page
                                pageNumber={pageNumber}
                                scale={scale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </Document>
                    </div>
                </div>

                <div className="resume-info">
                    <h3>简历说明</h3>
                    <div className="info-grid">
                        <div className="info-card">
                            <h4>📄 中文简历</h4>
                            <p>详细展示了我的教育背景、实习经历、项目经验和技能专长，适合国内企业申请。</p>
                        </div>
                        <div className="info-card">
                            <h4>📄 English Resume</h4>
                            <p>Comprehensive overview of my education, internship experience, projects and skills, suitable for international companies.</p>
                        </div>
                        <div className="info-card">
                            <h4>💼 产品经理匹配度</h4>
                            <p>简历中突出展示了与产品经理职位高度相关的经验，包括用户研究、产品设计、数据分析等核心技能。</p>
                        </div>
                        <div className="info-card">
                            <h4>🎯 核心优势</h4>
                            <p>技术背景 + 产品思维 + 实习经验 + 项目实践，形成了完整的产品经理能力体系。</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Resume;
